import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import BrushIcon from '../../../assets/svg/brush.svg?react';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectBusinessProfileStatus } from '../../slices/business-profile/businessProfileSlice';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import styles from './CustomerDetailsCard.module.scss';
import CurrencySelector from '../currency-selector/CurrencySelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { Customer } from '../../models/customer.model';
import { updateCustomers } from '../../slices/customers/customersSlice';
import RoomPreferencesOutlinedIcon from '@mui/icons-material/RoomPreferencesOutlined';
import { AccountCircleOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { NWClient } from '../../client/NWClient';
import { hideOverlay, showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { useCheckPermission } from '../hooks/useCheckPermission';
import { USER_PERMISSIONS } from '../constants/permissions';

interface CustomerDetailsCardProps {
  simplified?: boolean;
  preferences?: boolean;
  customer: Customer;
}

export default function CustomerDetailsCard({
  simplified,
  preferences,
  customer,
}: CustomerDetailsCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isEdited, setIsEdited] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);
  const [popperNode, setPopperNode] = useState<HTMLDivElement | null>(null);
  const [contactMethodIds, setContactMethodIds] = useState<any>({
    phone_number_id: null,
    email_address_id: null,
    social_media_id: null,
  });

  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);

  const isLoading = businessProfileStatus === 'loading';

  const toggleEditing = () => {
    setIsEdited((prev) => !prev);
  };

  const [formData, setFormData] = useState<Partial<Customer>>({
    id: customer?.id,
    customer_name: customer?.customer_name,
    contact_name: customer?.contact_name,
    contact_number: customer?.contact_number,
    email_address: customer?.email_address,
    physical_address: customer?.physical_address,
    postal_address: customer?.postal_address,
    industy_type: customer?.industy_type,
    website: customer?.website,
    customer_registration: customer?.customer_registration || '',
    preferred_contact_method: customer?.preferred_contact_method,
    preferred_currency: customer?.preferred_currency || 'AUD',
  });

  const [contactFormData, setContactFormData] = useState<any>({
    phone_number: undefined,
    email_address: undefined,
    social_media: undefined,
  });

  const checkPermission = useCheckPermission();

  const cancelChanges = () => {
    if (simplified && preferences) {
      if (customer?.contact_details) {
        const phoneDetail = customer.contact_details.find(
          (detail) => detail.detail_type === 'phone_number'
        );
        const emailDetail = customer.contact_details.find(
          (detail) => detail.detail_type === 'email_address'
        );
        const socialMediaDetail = customer.contact_details.find(
          (detail) => detail.detail_type === 'social_media'
        );

        setContactFormData({
          phone_number: phoneDetail?.detail,
          email_address: emailDetail?.detail,
          social_media: socialMediaDetail?.detail,
        });
        setFormData({
          ...formData,
          preferred_currency: customer?.preferred_currency || 'AUD',
        });
      }
    } else {
      setFormData({
        ...formData,
        id: customer?.id,
        customer_name: customer?.customer_name,
        contact_name: customer?.contact_name,
        contact_number: customer?.contact_number,
        email_address: customer?.email_address,
        physical_address: customer?.physical_address,
        postal_address: customer?.postal_address,
        industy_type: customer?.industy_type,
        website: customer?.website,
        customer_registration: customer?.customer_registration || '',
        //preferred_contact_method: customer?.preferred_contact_method,
        // preferred_currency: customer?.preferred_currency || 'AUD',
      });
    }
    setIsEdited(false);
  };

  useEffect(() => {
    if (customer?.contact_details) {
      const phoneDetail = customer.contact_details.find(
        (detail) => detail.detail_type === 'phone_number'
      );
      const emailDetail = customer.contact_details.find(
        (detail) => detail.detail_type === 'email_address'
      );
      const socialMediaDetail = customer.contact_details.find(
        (detail) => detail.detail_type === 'social_media'
      );

      setContactFormData({
        phone_number: phoneDetail?.detail || undefined,
        email_address: emailDetail?.detail || undefined,
        social_media: socialMediaDetail?.detail || undefined,
      });
      setContactMethodIds({
        phone_number_id: phoneDetail?.id || null,
        email_address_id: emailDetail?.id || null,
        social_media_id: socialMediaDetail?.id || null,
      });
    }
  }, [customer]);

  const handleContactChange = (field: keyof typeof contactFormData) => (value: string) => {
    setContactFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
    setIsEdited(true); // Mark the form as edited
  };

  // I couldn't think of a way to better refactor this method
  const saveContactChanges = (contactFormData: {
    phone_number?: string;
    email_address?: string;
    social_media?: string;
  }) => {
    if (!contactFormData) {
      toast.error(t('messages.invalidFormData'));
      return;
    }

    const phoneDetail = customer.contact_details?.find(
      (detail) => detail.detail_type === 'phone_number'
    );
    const emailDetail = customer.contact_details?.find(
      (detail) => detail.detail_type === 'email_address'
    );
    const socialMediaDetail = customer.contact_details?.find(
      (detail) => detail.detail_type === 'social_media'
    );

    const { phone_number, email_address, social_media } = contactFormData;

    let data = null;
    let contactId = null;

    if (phone_number && phone_number.trim() !== '' && phone_number.trim() !== phoneDetail?.detail) {
      data = { detail_type: 'phone_number', detail: phone_number, customer_id: customer.id };
      contactId = contactMethodIds.phone_number_id;
      NWClient.put('customer-contact-details', contactId, data, true, true).catch(() => {
        toast.error(t('messages.errorOccurred'), { theme: 'colored' });
      });
    }
    if (
      email_address &&
      email_address.trim() !== '' &&
      email_address.trim() !== emailDetail?.detail
    ) {
      data = { detail_type: 'email_address', detail: email_address, customer_id: customer.id };
      contactId = contactMethodIds.email_address_id;
      NWClient.put('customer-contact-details', contactId, data, true, true).catch(() => {
        toast.error(t('messages.errorOccurred'));
      });
    }
    if (
      social_media &&
      social_media.trim() !== '' &&
      social_media.trim() !== socialMediaDetail?.detail
    ) {
      data = { detail_type: 'social_media', detail: social_media, customer_id: customer.id };
      contactId = contactMethodIds.social_media_id;
      NWClient.put('customer-contact-details', contactId, data, true, true).catch(() => {
        toast.error(t('messages.errorOccurred'), { theme: 'colored' });
      });
    } else {
      return;
    }

    NWClient.put('customer-contact-details', contactId, data, true, true).catch(() => {
      toast.error(t('messages.errorOccurred'));
    });
  };

  const saveChanges = () => {
    if (!customer?.id) return;
    dispatch(showOverlay());
    const payload = {
      customer_name: formData.customer_name,
      contact_name: formData.contact_name,
      contact_number: formData.contact_number,
      email_address: formData.email_address,
      physical_address: formData.physical_address,
      postal_address: formData.postal_address,
      industy_type: formData.industy_type,
      website: formData.website,
      preferred_contact_method: formData.preferred_contact_method,
      preferred_currency: formData.preferred_currency,
    };

    saveContactChanges(contactFormData);

    dispatch(
      updateCustomers({
        id: customer.id,
        company: customer.company,
        ...payload,
      })
    )
      .unwrap()
      .finally(() => dispatch(hideOverlay()));
    setIsEdited(false);
    setShowModal(false);
  };

  const confirmExit = () => {
    setIsEdited(false);
    setShowModal(false);
  };

  const handleChange =
    <K extends keyof Customer>(field: K) =>
    (value: Customer[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEdited &&
        formRef.current &&
        !formRef.current.contains(event.target as Node) &&
        (!popperNode || !popperNode.contains(event.target as Node)) // Ahora ignora correctamente los clics en el menú
      ) {
        const targetElement = event.target as HTMLElement;

        if (
          targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
          targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
        ) {
          return;
        }
        setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEdited, popperNode]);

  return (
    <Grid container className='inputs-editable'>
      <Grid size={{ xs: 12 }}>
        <Card
          className={`${styles.card} ${isEdited ? styles.card_border : ''} ${
            preferences &&
            Object.values(contactFormData).filter((val) => val === undefined).length >= 2 &&
            styles.preferences_card
          }`}
          ref={formRef}
        >
          <CardContent
            className={isLoading ? styles.card_content_min_height : ''}
            sx={{ paddingTop: 0, paddingBottom: '0 !important' }}
          >
            <Grid
              container
              alignItems='center'
              justifyContent='space-between'
              paddingLeft='0 !important'
              paddingRight='0 !important'
              minHeight='76px'
              flexWrap='nowrap'
              p={2}
            >
              {!preferences ? (
                <Grid container alignItems='center' gap={1}>
                  <Grid sx={{ marginBottom: '0.2rem' }}>
                    <AccountCircleOutlined />
                  </Grid>
                  <Grid>
                    <Typography variant='h6' className='semibold-variant'>
                      {t('labels.customerDetails')}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid container alignItems='center' gap={1}>
                  <Grid sx={{ marginBottom: '0.2rem' }}>
                    <RoomPreferencesOutlinedIcon />
                  </Grid>
                  <Grid>
                    <Typography variant='h6' className='semibold-variant'>
                      {t('labels.preferences')}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              <Grid size={{ xs: 1 }} container justifyContent='flex-end'>
                {!isEdited ? (
                  <Tooltip
                    className='flex-shrink-0'
                    title={
                      checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_UPDATE)
                        ? t('buttons.edit')
                        : t('messages.contactAdministrator')
                    }
                    placement='top'
                  >
                    <IconButton
                      onClick={toggleEditing}
                      disabled={!checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_UPDATE)}
                    >
                      <BrushIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Box display='flex' gap='8px' alignItems='center'>
                    <Button
                      type='button'
                      className='btn-default btn-action-basic btn-cancel'
                      role='button'
                      variant='contained'
                      size='large'
                      onClick={cancelChanges}
                    >
                      {t('buttons.cancel')}
                    </Button>
                    <Button
                      type='submit'
                      className='btn-default btn-action-basic'
                      role='button'
                      disabled={!isEdited}
                      variant='contained'
                      size='large'
                      onClick={saveChanges}
                    >
                      {t('buttons.save')}
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>

            {!simplified ? (
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='customer-name'
                    label={t('labels.customerName')}
                    variant='outlined'
                    fullWidth
                    value={formData.customer_name ?? ''}
                    onChange={(e) => handleChange('customer_name')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='contact-name'
                    label={t('labels.contactName')}
                    variant='outlined'
                    fullWidth
                    value={formData.contact_name ?? ''}
                    onChange={(e) => handleChange('contact_name')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    label={t('labels.phoneNumber')}
                    variant='outlined'
                    fullWidth
                    value={formData.contact_number ?? ''}
                    onChange={(e) => handleChange('contact_number')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    label={t('labels.emailAddress')}
                    variant='outlined'
                    fullWidth
                    value={formData.email_address ?? ''}
                    onChange={(e) => handleChange('email_address')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='customer-registration'
                    fullWidth
                    label={`${t('labels.customerRegistrationNumber')}`}
                    onChange={(e) => handleChange('customer_registration')(e.target.value)}
                    name='customer_registration'
                    disabled={!isEdited}
                    value={formData.customer_registration}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='physical-address'
                    label={t('formInputs.physicalAddressInputLabel')}
                    variant='outlined'
                    fullWidth
                    value={formData.physical_address ?? ''}
                    onChange={(e) => handleChange('physical_address')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='postal-address'
                    label={t('formInputs.postalAddressInputLabel')}
                    variant='outlined'
                    fullWidth
                    value={formData.postal_address ?? ''}
                    onChange={(e) => handleChange('postal_address')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='industry-type'
                    label={t('labels.industryType')}
                    variant='outlined'
                    fullWidth
                    value={formData.industy_type ?? ''}
                    onChange={(e) => handleChange('industy_type')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <TextField
                    id='website'
                    label={t('labels.website')}
                    variant='outlined'
                    fullWidth
                    value={formData.website ?? ''}
                    onChange={(e) => handleChange('website')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
              </Grid>
            ) : (
              simplified &&
              !preferences && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      id='customer-name'
                      label={t('labels.customerName')}
                      variant='outlined'
                      fullWidth
                      value={formData.customer_name ?? ''}
                      onChange={(e) => handleChange('customer_name')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      label={t('labels.phoneNumber')}
                      variant='outlined'
                      fullWidth
                      value={formData.contact_number ?? ''}
                      onChange={(e) => handleChange('contact_number')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      label={t('labels.emailAddress')}
                      variant='outlined'
                      fullWidth
                      value={formData.email_address ?? ''}
                      onChange={(e) => handleChange('email_address')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      id='industry-type'
                      label={t('labels.industryType')}
                      variant='outlined'
                      fullWidth
                      value={formData.industy_type ?? ''}
                      onChange={(e) => handleChange('industy_type')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                </Grid>
              )
            )}
            {simplified && preferences && (
              <Grid container spacing={2}>
                {contactFormData.phone_number !== undefined && (
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      label={t('labels.phoneNumber')}
                      variant='outlined'
                      fullWidth
                      value={contactFormData.phone_number}
                      onChange={(e) => handleContactChange('phone_number')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                )}
                {contactFormData.email_address !== undefined && (
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      label={t('labels.emailAddress')}
                      variant='outlined'
                      fullWidth
                      value={contactFormData.email_address}
                      onChange={(e) => handleContactChange('email_address')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                )}
                {contactFormData.social_media !== undefined && (
                  <Grid size={{ xs: 6 }}>
                    {' '}
                    <TextField
                      id='social-media'
                      label={t('labels.socialMedia')}
                      variant='outlined'
                      fullWidth
                      value={contactFormData.social_media}
                      onChange={(e) => handleContactChange('social_media')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                )}
                <Grid size={{ xs: 6 }}>
                  {' '}
                  <CurrencySelector
                    value={formData.preferred_currency}
                    onChange={handleChange('preferred_currency')}
                    disabled={!isEdited}
                    setPopperRef={setPopperNode}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
    </Grid>
  );
}
